import { take, tap, switchMap, map, distinctUntilChanged, catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Task } from '../models/task/task.model';
import { TaskFactory } from '../models/task/task-factory';
import { ApiHttpClient } from 'app/http/clients/api';
import { HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';
import { CallActivity } from '../models/call-activity/call-activity.model';
import { LegacyTask } from '../models/task/legacy-task.model';
import { TaskTypeCount } from '../models/task-type-count';
import { Store } from '@ngxs/store';
import { UpdateInboxBadgeCount } from '../../../store/task-management/task-management.actions';


interface TasksAndCounts extends LegacyTask {
  tasks: Task[];
  counts?: TaskTypeCount[];
}

@Injectable()
export class TaskService {


    private tasksUrl: string;
    private taskFactory: TaskFactory;
    private filteredTotalTaskCount$: Observable<number>;
    private tasks$: BehaviorSubject<Task[]> = new BehaviorSubject([]);
    private tasksAndCounts$: BehaviorSubject<any> = new BehaviorSubject({tasks: [], counts: []});
    private filteredTasks$: BehaviorSubject<Task[]> = new BehaviorSubject([]);

    constructor(
      private http: ApiHttpClient,
      private store: Store
    ) {
        this.tasksUrl = '/inbox-task';
        this.taskFactory = new TaskFactory();
        this.filteredTotalTaskCount$ = this.filteredTasks$.pipe(map(tasks => tasks.length));
    }

    get(params: {
      userId?: number;
      taskType?: string;
      callType?: string;
      sortBy?: object;
      action?: string;
    } = {}): Observable<TasksAndCounts[]> {

        let httpParams = new HttpParams();
        if (params?.userId) {
            httpParams = httpParams.set('userId', params.userId.toString());
        }

        if (params?.taskType) {
            httpParams = httpParams.set('taskType', params.taskType.toString());
        }

        if (params?.callType) {
            httpParams = httpParams.set('callType', params.callType.toString());
        }

        if (params?.sortBy) {
            httpParams = httpParams.set('sortBy', JSON.stringify(params.sortBy));
        }

        if (params?.action) {
          httpParams = httpParams.set('action', params.action);
        }

        return this.http.get(this.tasksUrl, { params: httpParams }).pipe(
          switchMap((taskObjs: TasksAndCounts) => {
            const tasksObject = taskObjs.tasks;
            const filteredTasks: Task[] = [];
            const tasks = tasksObject.map((taskJson: Task) => {
              if (taskJson.type !== 'callActivity') {
                filteredTasks.push(taskJson);
              }
                return this.taskFactory.fromJson(taskJson);
            });
            if (params?.taskType === 'myTasks') {
              this.store.dispatch(new UpdateInboxBadgeCount(tasks.length));
            }
            this.tasks$.next(tasks);
            this.tasksAndCounts$.next(taskObjs);
            this.filteredTasks$.next(filteredTasks);
            return this.tasksAndCounts$.asObservable();
          })
        );
    }

    get tasks(): Observable<Task[]> { return this.tasks$.asObservable().pipe(distinctUntilChanged()); }

    get tasksAndCounts(): Observable<Task[]> { return this.tasksAndCounts$.asObservable(); }

    get totalTaskCount(): Observable<number> { return this.filteredTotalTaskCount$.pipe(distinctUntilChanged()); }

    dismiss(task: LegacyTask): Observable<Task[]> {
      return this.http.put(this.tasksUrl + '/dismiss', task).pipe(
        catchError(() => of([])),
        switchMap(() => this.tasks$),
        take(1),
        tap((tasks) => {
            const i = tasks.findIndex((currentTask) =>  task.taskObjectId === (currentTask as unknown as LegacyTask).taskObjectId );
            tasks.splice(i, 1);
            this.tasks$.next(tasks);
        })
      );
    }

    callActivityViewed(id: number): Observable<CallActivity> {
        return this.http.put<CallActivity>(`${environment.API_URL}/call-viewed/${id}`, {});
    }
}

export interface TaskSearchParams {
    page?: number;
    pageSize?: number;
    taskType?: string;
    userId?: number;
    callType?: string;
    sortBy?: object;
}
